import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  platformAdminRbacApi,
  type PlatformMenuCatalogResponse,
  type PlatformPermissionDef,
  type PlatformRoleRow,
} from '@/api/platform-admin-rbac';
import { platformNavMenuApi, type NavMenuNode } from '@/api/platform-nav-menu';
import { refreshSessionFromServer } from '@/lib/session-sync';
import { RoleMenuCheckboxes } from '@/components/rbac/RoleMenuCheckboxes';
import {
  RoleMenuTreeCheckboxes,
  filterNavTreeForRolePicker,
} from '@/components/rbac/RoleMenuTreeCheckboxes';
import { toast } from 'sonner';
import { useUIStore } from '@/store/useUIStore';
import {
  adminDrawerFooterClass,
  adminDrawerHeaderClass,
  adminDrawerShell,
  btnOutlineNeon,
  btnPrimaryNeon,
} from '@/lib/admin-ui';
import { cn } from '@/lib/utils';
import { usePlatformPermission } from '@/hooks/use-platform-permission';

const PLATFORM_ROLE_ADMIN_ID = 'role-admin';

/** 右侧 Drawer 内表单：避免 Input disabled 时 opacity-50 导致文字不可见 */
const drawerInputClass =
  'border-white/10 bg-zinc-900/60 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-cyan-500/35 disabled:cursor-not-allowed disabled:opacity-100 disabled:bg-zinc-900/95 disabled:text-zinc-300';
const drawerLabelClass = 'text-zinc-200';

function derivePermissionCodesFromMenuPaths(
  menuPaths: Set<string>,
  menuCatalog: PlatformMenuCatalogResponse['items'],
) {
  const out = new Set<string>();
  [...menuPaths].forEach((path) => {
    const item = menuCatalog.find((m) => m.path === path);
    (item?.permissions ?? []).forEach((p) => out.add(p));
  });
  return [...out];
}

function inferMenuPathsFromPermissionCodes(
  codes: Set<string>,
  menuCatalog: PlatformMenuCatalogResponse['items'],
) {
  const out = new Set<string>();
  menuCatalog.forEach((item) => {
    if ((item.permissions ?? []).some((p) => codes.has(p))) {
      out.add(item.path);
    }
  });
  return out;
}
export function PlatformRolesPage() {
  const isLight = useUIStore((s) => s.theme === 'light');
  const lightPrimaryBtn =
    'border border-cyan-400/45 bg-cyan-500 text-slate-950 hover:bg-cyan-400';
  const lightDrawerField =
    'border-slate-300 bg-white text-slate-800 placeholder:text-slate-400 focus-visible:ring-cyan-500/35 disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300';
  const lightDrawerLabel = 'text-slate-700';
  const canManagePlatformRbac = usePlatformPermission(
    'platform:platform_user:manage',
  );
  const [defs, setDefs] = useState<PlatformPermissionDef[]>([]);
  const [roles, setRoles] = useState<PlatformRoleRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState<PlatformRoleRow | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formMenuPaths, setFormMenuPaths] = useState<Set<string>>(new Set());
  const [menuCatalog, setMenuCatalog] = useState<
    PlatformMenuCatalogResponse['items']
  >([]);
  /** 非空时用树形勾选（与 nav_menus 一致）；否则回退扁平 catalog */
  const [menuTreeForPicker, setMenuTreeForPicker] = useState<NavMenuNode[] | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [expandedRoleIds, setExpandedRoleIds] = useState<Set<string>>(new Set());
  const menuDrivenCodes = useMemo(
    () => derivePermissionCodesFromMenuPaths(formMenuPaths, menuCatalog),
    [formMenuPaths, menuCatalog],
  );
  const permissionLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    defs.forEach((d) => map.set(d.code, d.name?.trim() || d.code));
    return map;
  }, [defs]);

  async function load() {
    try {
      setLoading(true);
      const [p, r, cat, rawTree] = await Promise.all([
        platformAdminRbacApi.listPermissions(),
        platformAdminRbacApi.listRoles(),
        platformAdminRbacApi.getMenuCatalog(),
        platformNavMenuApi.getTree('PLATFORM').catch(() => null as NavMenuNode[] | null),
      ]);
      setDefs(p.data);
      setRoles(r.data);
      setMenuCatalog(cat.data.items);
      if (rawTree?.length) {
        const filtered = filterNavTreeForRolePicker(rawTree);
        setMenuTreeForPicker(filtered.length > 0 ? filtered : null);
      } else {
        setMenuTreeForPicker(null);
      }
    } catch {
      toast.error('加载平台角色失败');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function openCreate() {
    setFormName('');
    setFormDesc('');
    setFormMenuPaths(new Set());
    setCreateOpen(true);
  }

  function openEdit(role: PlatformRoleRow) {
    if (role.id === PLATFORM_ROLE_ADMIN_ID) {
      toast.info('内置平台超级管理员角色不可编辑');
      return;
    }
    setEditRole(role);
    setFormName(role.name);
    setFormDesc(role.description ?? '');
    const codeSet = new Set(role.permissions.map((x) => x.permission.code));
    const menuKeys = (role.menuItems ?? []).map((m) => m.menuKey);
    /** 内置超管：库中无 RoleMenuItem 时等价「全侧栏」；表单展示为全选以与语义一致 */
    if (role.id === PLATFORM_ROLE_ADMIN_ID && menuKeys.length === 0) {
      setFormMenuPaths(new Set(menuCatalog.map((i) => i.path)));
    } else if (menuKeys.length === 0) {
      setFormMenuPaths(inferMenuPathsFromPermissionCodes(codeSet, menuCatalog));
    } else {
      setFormMenuPaths(new Set(menuKeys));
    }
  }

  function toggleMenuPath(path: string) {
    setFormMenuPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  async function submitCreate() {
    const codes = [...new Set(menuDrivenCodes)];
    if (!formName.trim() || formMenuPaths.size === 0 || !codes.length) {
      toast.error('请填写名称并至少勾选一个菜单入口');
      return;
    }
    try {
      setSaving(true);
      const { data: created } = await platformAdminRbacApi.createRole({
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        permissionCodes: codes,
      });
      await platformAdminRbacApi.setRoleMenu(created.id, [...formMenuPaths]);
      toast.success('已创建角色');
      setCreateOpen(false);
      await load();
      await refreshSessionFromServer();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '创建失败';
      toast.error(typeof msg === 'string' ? msg : '创建失败');
    } finally {
      setSaving(false);
    }
  }

  async function submitEdit() {
    if (!editRole) return;
    const codes = [...new Set(menuDrivenCodes)];
    if (formMenuPaths.size === 0 || !codes.length) {
      toast.error('请至少勾选一个菜单入口');
      return;
    }
    try {
      setSaving(true);
      const builtin = editRole.id === PLATFORM_ROLE_ADMIN_ID;
      await platformAdminRbacApi.updateRole(editRole.id, {
        ...(!builtin ? { name: formName.trim() } : {}),
        description: formDesc.trim() || undefined,
        permissionCodes: codes,
      });
      await platformAdminRbacApi.setRoleMenu(editRole.id, [...formMenuPaths]);
      toast.success('已保存');
      setEditRole(null);
      await load();
      await refreshSessionFromServer();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '保存失败';
      toast.error(typeof msg === 'string' ? msg : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    try {
      await platformAdminRbacApi.deleteRole(deleteId);
      toast.success('已删除');
      setDeleteId(null);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '删除失败';
      toast.error(typeof msg === 'string' ? msg : '删除失败');
    }
  }

  function toggleRolePermissionExpand(roleId: string) {
    setExpandedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">平台角色</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          配置运营后台可分配的平台角色与权限；超级管理员角色须保留 <code className="text-cyan-300/80">platform:*</code>
          。创建平台用户时可在「平台用户」页选择角色。
        </p>
      </div>

      <Card className={cn(isLight ? 'border-slate-300 bg-white' : 'border-white/10 bg-zinc-900/40')}>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">角色列表</CardTitle>
            <CardDescription>共 {roles.length} 个角色</CardDescription>
          </div>
          {canManagePlatformRbac && (
            <Button type="button" className={btnPrimaryNeon} onClick={openCreate}>
              新建角色
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">加载中…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={cn(isLight ? 'border-slate-300 hover:bg-transparent' : 'border-white/10 hover:bg-transparent')}>
                  <TableHead>名称</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>功能权限</TableHead>
                  <TableHead>账号数</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((r) => {
                  const builtin = r.id === PLATFORM_ROLE_ADMIN_ID;
                  const labels = r.permissions.map(
                    (x) => permissionLabelMap.get(x.permission.code) || x.permission.code,
                  );
                  const expanded = expandedRoleIds.has(r.id);
                  const summaryLabels = labels.slice(0, 3);
                  const moreCount = Math.max(0, labels.length - summaryLabels.length);
                  return (
                    <TableRow key={r.id} className={cn(isLight ? 'border-slate-300' : 'border-white/10')}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        {builtin ? (
                          <Badge
                            variant="outline"
                            className={cn(
                              isLight ? 'border-slate-300 text-slate-600' : 'border-white/15 text-zinc-300',
                            )}
                          >
                            内置
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className={cn(
                              isLight
                                ? 'border-cyan-300/45 text-cyan-700'
                                : 'border-cyan-500/30 text-cyan-200/90',
                            )}
                          >
                            自定义
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className={cn('max-w-md text-xs', isLight ? 'text-slate-500' : 'text-zinc-400')}>
                        {expanded ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            {labels.map((label) => (
                              <span
                                key={label}
                                className={cn(
                                  'rounded-full border px-2 py-0.5',
                                  isLight
                                    ? 'border-cyan-300/45 bg-cyan-50 text-cyan-700'
                                    : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200/90',
                                )}
                              >
                                {label}
                              </span>
                            ))}
                            {labels.length > 3 && (
                              <button
                                type="button"
                                className={cn(
                                  'rounded border px-2 py-0.5 text-[11px] transition-colors',
                                  isLight
                                    ? 'border-slate-300 text-slate-500 hover:border-cyan-400/45 hover:text-cyan-700'
                                    : 'border-white/15 text-zinc-500 hover:border-cyan-500/35 hover:text-cyan-200',
                                )}
                                onClick={() => toggleRolePermissionExpand(r.id)}
                              >
                                收起
                              </button>
                            )}
                          </div>
                        ) : summaryLabels.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            {summaryLabels.map((label) => (
                              <span
                                key={label}
                                className={cn(
                                  'rounded-full border px-2 py-0.5',
                                  isLight
                                    ? 'border-cyan-300/45 bg-cyan-50 text-cyan-700'
                                    : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200/90',
                                )}
                              >
                                {label}
                              </span>
                            ))}
                            {moreCount > 0 && (
                              <button
                                type="button"
                                className={cn(isLight ? 'text-slate-500' : 'text-zinc-500')}
                                onClick={() => toggleRolePermissionExpand(r.id)}
                              >
                                +{moreCount} 项
                              </button>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{r._count.users}</TableCell>
                      <TableCell className="text-right">
                        {canManagePlatformRbac ? (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={btnOutlineNeon}
                              disabled={builtin}
                              title={builtin ? '内置平台超级管理员不可编辑' : undefined}
                              onClick={() => openEdit(r)}
                            >
                              编辑
                            </Button>
                            {!builtin && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={`ml-2 ${btnOutlineNeon}`}
                                disabled={r._count.users > 0}
                                title={r._count.users > 0 ? '仍有账号绑定' : undefined}
                                onClick={() => setDeleteId(r.id)}
                              >
                                删除
                              </Button>
                            )}
                          </>
                        ) : (
                          <span className={cn('text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Drawer open={createOpen} onOpenChange={setCreateOpen} direction='right'>
        <DrawerContent
          className={cn(
            adminDrawerShell('wide'),
            'border-0 p-0 [&>button]:!right-5 [&>button]:!top-5 [&>button]:rounded-xl [&>button]:border [&>button]:p-2 [&>button]:opacity-100',
            isLight
              ? '[&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/45 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700'
              : '[&>button]:border-white/10 [&>button]:bg-zinc-900/90 [&>button]:text-zinc-400 [&>button]:hover:border-cyan-500/30 [&>button]:hover:bg-cyan-500/10 [&>button]:hover:text-cyan-200',
          )}
        >
          <DrawerHeader className={cn(adminDrawerHeaderClass, isLight ? 'border-b border-slate-200 bg-white' : 'border-b border-white/10')}>
            <DrawerTitle className={cn(isLight ? 'text-slate-900' : 'text-zinc-50')}>新建平台角色</DrawerTitle>
            <DrawerDescription className={cn(isLight ? 'text-slate-500' : 'text-zinc-300')}>
              权限码均为 PLATFORM 作用域。
            </DrawerDescription>
          </DrawerHeader>
          <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4', isLight ? 'bg-white' : 'bg-zinc-950')}>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="nr-name" className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>
                  名称
                </Label>
                <Input
                  id="nr-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className={cn(drawerInputClass, isLight && lightDrawerField)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nr-desc" className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>
                  说明（可选）
                </Label>
                <Input
                  id="nr-desc"
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  className={cn(drawerInputClass, isLight && lightDrawerField)}
                />
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>功能与导航权限</Label>
                <div
                  className={cn(
                    'rounded-md border px-3 py-2 text-xs',
                    isLight
                      ? 'border-cyan-300/40 bg-cyan-50 text-slate-600'
                      : 'border-cyan-500/20 bg-cyan-500/5 text-zinc-300',
                  )}
                >
                  已选 {menuDrivenCodes.length} 项功能权限。
                </div>
                <p className={cn('text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                  勾选菜单即自动授予对应功能权限；取消菜单会同步回收关联权限。
                </p>
                {menuTreeForPicker ? (
                  <RoleMenuTreeCheckboxes
                    tree={menuTreeForPicker}
                    value={formMenuPaths}
                    onToggle={toggleMenuPath}
                    accent="cyan"
                  />
                ) : (
                  <RoleMenuCheckboxes
                    items={menuCatalog}
                    value={formMenuPaths}
                    onToggle={toggleMenuPath}
                  />
                )}
              </div>
            </div>
          </div>
          <DrawerFooter className={cn(adminDrawerFooterClass, isLight ? 'border-slate-200 bg-white' : 'border-white/10')}>
            <Button
              type="button"
              className={cn('w-full', isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              disabled={saving}
              onClick={() => void submitCreate()}
            >
              {saving ? '提交中…' : '创建'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer
        open={!!editRole}
        onOpenChange={(open) => {
          if (!open) setEditRole(null);
        }}
        direction='right'
      >
        <DrawerContent
          className={cn(
            adminDrawerShell('wide'),
            'border-0 p-0 [&>button]:!right-5 [&>button]:!top-5 [&>button]:rounded-xl [&>button]:border [&>button]:p-2 [&>button]:opacity-100',
            isLight
              ? '[&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/45 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700'
              : '[&>button]:border-white/10 [&>button]:bg-zinc-900/90 [&>button]:text-zinc-400 [&>button]:hover:border-cyan-500/30 [&>button]:hover:bg-cyan-500/10 [&>button]:hover:text-cyan-200',
          )}
        >
          <DrawerHeader className={cn(adminDrawerHeaderClass, isLight ? 'border-b border-slate-200 bg-white' : 'border-b border-white/10')}>
            <DrawerTitle className={cn(isLight ? 'text-slate-900' : 'text-zinc-50')}>编辑平台角色</DrawerTitle>
            <DrawerDescription className={cn(isLight ? 'text-slate-500' : 'text-zinc-300')}>
              内置超级管理员须保留 platform:*
            </DrawerDescription>
          </DrawerHeader>
          {editRole && (
            <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4', isLight ? 'bg-white' : 'bg-zinc-950')}>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="er-name" className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>
                    名称
                  </Label>
                  <Input
                    id="er-name"
                    value={formName}
                    disabled={editRole.id === PLATFORM_ROLE_ADMIN_ID}
                    onChange={(e) => setFormName(e.target.value)}
                    className={cn(drawerInputClass, isLight && lightDrawerField)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="er-desc" className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>
                    说明（可选）
                  </Label>
                  <Input
                    id="er-desc"
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                    className={cn(drawerInputClass, isLight && lightDrawerField)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>功能与导航权限</Label>
                  <div
                    className={cn(
                      'rounded-md border px-3 py-2 text-xs',
                      isLight
                        ? 'border-cyan-300/40 bg-cyan-50 text-slate-600'
                        : 'border-cyan-500/20 bg-cyan-500/5 text-zinc-300',
                    )}
                  >
                    已选 {menuDrivenCodes.length} 项功能权限。
                  </div>
                  <p className={cn('text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                    勾选菜单即自动授予对应功能权限；取消菜单会同步回收关联权限。
                  </p>
                  {menuTreeForPicker ? (
                    <RoleMenuTreeCheckboxes
                      tree={menuTreeForPicker}
                      value={formMenuPaths}
                      onToggle={toggleMenuPath}
                      accent="cyan"
                    />
                  ) : (
                    <RoleMenuCheckboxes
                      items={menuCatalog}
                      value={formMenuPaths}
                      onToggle={toggleMenuPath}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          <DrawerFooter className={cn(adminDrawerFooterClass, isLight ? 'border-slate-200 bg-white' : 'border-white/10')}>
            <Button
              type="button"
              className={cn('w-full', isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              disabled={saving}
              onClick={() => void submitEdit()}
            >
              {saving ? '保存中…' : '保存'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="border-white/10 bg-zinc-950 text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>删除该平台角色？</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              仅当无运营账号绑定时可删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={btnOutlineNeon}>取消</AlertDialogCancel>
            <AlertDialogAction
              className={btnPrimaryNeon}
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
